import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Button as MuiButton,
  useMediaQuery,
  Paper,
  Divider,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/joy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import BusinessIcon from "@mui/icons-material/Business";
import CategoryIcon from "@mui/icons-material/Category";
import { Input } from "@mui/joy";
import { DataGrid } from "@mui/x-data-grid";
import { Get, Post, Delete, Put, PostUpload } from "../../../services/apiServices";
import { networkUrls } from "../../../services/networkurls";
import ReuseableButton from "../../../components/Button/ReusableButton";
import ReuseableSnackbar from "../../../components/Snackbar/Snackbar";

const VisuallyHiddenInput = styled("input")`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1px;
`;

interface Industry { id: number; name: string; }
interface Category { id: number; name: string; }
interface Document {
  doc_id: string;
  file_name: string;
  industry_name: string;
  category: string;
  title: string;
  drive_link: string | null;
  point_id: string;
}

const ACCENT = "#4D8FAC";
const ACCENT_DARK = "#3b738a";
const ACCENT_LIGHT = "#eaf4f8";

const SectionCard = ({ children, sx = {} }: { children: React.ReactNode; sx?: any }) => (
  <Paper
    elevation={0}
    sx={{
      border: "1px solid #e0e0e0",
      borderRadius: "12px",
      p: 2.5,
      backgroundColor: "#fff",
      ...sx,
    }}
  >
    {children}
  </Paper>
);

const SectionHeader = ({
  icon,
  title,
  onAdd,
  addLabel,
}: {
  icon: React.ReactNode;
  title: string;
  onAdd: () => void;
  addLabel: string;
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      mb: 2,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "8px",
          backgroundColor: ACCENT_LIGHT,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: ACCENT,
        }}
      >
        {icon}
      </Box>
      <Typography fontWeight="700" fontSize="0.95rem" color="#1a1a1a">
        {title}
      </Typography>
    </Box>
    <MuiButton
      size="small"
      variant="contained"
      startIcon={<AddIcon fontSize="small" />}
      onClick={onAdd}
      sx={{
        backgroundColor: ACCENT,
        borderRadius: "8px",
        textTransform: "none",
        fontWeight: 600,
        fontSize: "0.8rem",
        height: "32px",
        px: 1.5,
        boxShadow: "none",
        "&:hover": { backgroundColor: ACCENT_DARK, boxShadow: "none" },
      }}
    >
      {addLabel}
    </MuiButton>
  </Box>
);

const IndustryTrackContent = () => {
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [industriesLoading, setIndustriesLoading] = useState(false);
  const [addIndustryOpen, setAddIndustryOpen] = useState(false);
  const [newIndustryName, setNewIndustryName] = useState("");
  const [addIndustryLoading, setAddIndustryLoading] = useState(false);

  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [addCategoryLoading, setAddCategoryLoading] = useState(false);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ type: "industry" | "category" | "document"; id: number | string; name: string } | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<{ type: "industry" | "category"; id: number; name: string } | null>(null);
  const [editName, setEditName] = useState("");
  const [editLoading, setEditLoading] = useState(false);

  const [editDocOpen, setEditDocOpen] = useState(false);
  const [editDocTarget, setEditDocTarget] = useState<{ doc_id: string; title: string; industry_name: string; category: string; file_name: string } | null>(null);
  const [editDocLoading, setEditDocLoading] = useState(false);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedTranscriptFile, setSelectedTranscriptFile] = useState<File | null>(null);
  const [selectedIndustry, setSelectedIndustry] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [uploadLoading, setUploadLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ industry?: string; category?: string; file?: string; transcript?: string }>({});

  const transcriptCategories = ["Demo Asset", "Demo Videos"];
  const showTranscript = transcriptCategories.includes(selectedCategory);

  const [documents, setDocuments] = useState<Document[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [triggerRefresh, setTriggerRefresh] = useState(0);

  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryData, setSummaryData] = useState<{
    doc_id: string;
    title: string;
    summary: string;
    implemented_solutions: string[];
    key_outcomes: string[];
    challenges_addressed: string[];
  } | null>(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({ color: "", message: "" });

  const isMobile = useMediaQuery("(max-width:768px)");

  const handleDocumentSummary = async (doc_id: string) => {
    setSummaryLoading(true);
    setSummaryData(null);
    setSummaryOpen(true);
    try {
      const res = await Post(networkUrls.documentSummary, { doc_id }, true);
      if (res?.status === 200) {
        setSummaryData(res.data);
      } else {
        setSummaryOpen(false);
        showSnackbar("error", "Failed to fetch document summary");
      }
    } catch {
      setSummaryOpen(false);
      showSnackbar("error", "Failed to fetch document summary");
    } finally {
      setSummaryLoading(false);
    }
  };

  const showSnackbar = (color: string, message: string) => {
    setSnackbarInfo({ color, message });
    setSnackbarOpen(true);
  };

  const fetchIndustries = async () => {
    setIndustriesLoading(true);
    try {
      const res = await Get(networkUrls.industryTrackContentIndustries, true);
      if (res?.status === 200) setIndustries(res.data.industries || []);
    } finally { setIndustriesLoading(false); }
  };

  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const res = await Get(networkUrls.industryTrackContentCategories, true);
      if (res?.status === 200) setCategories(res.data.categories || []);
    } finally { setCategoriesLoading(false); }
  };

  const fetchDocuments = async () => {
    setDocsLoading(true);
    try {
      let url = `${networkUrls.industryTrackContentDocuments}?page=${page + 1}&page_size=${pageSize}`;
      if (debouncedSearch.length >= 3) url += `&search=${encodeURIComponent(debouncedSearch)}`;
      const res = await Get(url, true);
      if (res?.status === 200) {
        setDocuments(res.data.documents || []);
        setTotalCount(res.data.total || 0);
      }
    } finally { setDocsLoading(false); }
  };

  useEffect(() => { fetchIndustries(); fetchCategories(); }, []);
  useEffect(() => { fetchDocuments(); }, [page, pageSize, debouncedSearch, triggerRefresh]);
  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(searchText.trim()), 400);
    return () => clearTimeout(h);
  }, [searchText]);

  const handleAddIndustry = async () => {
    if (!newIndustryName.trim()) return;
    setAddIndustryLoading(true);
    try {
      const res = await Post(networkUrls.industryTrackContentIndustries, { name: newIndustryName.trim() }, true);
      if (res?.status === 200) {
        showSnackbar("success", res.data?.message || `Industry '${newIndustryName.trim()}' added`);
        setNewIndustryName(""); setAddIndustryOpen(false); fetchIndustries();
      }
    } finally { setAddIndustryLoading(false); }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    setAddCategoryLoading(true);
    try {
      const res = await Post(networkUrls.industryTrackContentCategories, { name: newCategoryName.trim() }, true);
      if (res?.status === 200) {
        showSnackbar("success", res.data?.message || `Category '${newCategoryName.trim()}' added`);
        setNewCategoryName(""); setAddCategoryOpen(false); fetchCategories();
      }
    } finally { setAddCategoryLoading(false); }
  };

  const openDeleteConfirm = (type: "industry" | "category" | "document", id: number | string, name: string) => {
    setDeleteTarget({ type, id, name });
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      let url = deleteTarget.type === "industry"
        ? `${networkUrls.industryTrackContentIndustries}/${deleteTarget.id}`
        : deleteTarget.type === "category"
        ? `${networkUrls.industryTrackContentCategories}/${deleteTarget.id}`
        : `${networkUrls.industryTrackContentDeleteDocument}/${deleteTarget.id}`;

      const res = await Delete(url, true);
      if (res?.status === 200) {
        showSnackbar("success", res.data?.message || `'${deleteTarget.name}' deleted successfully`);
        if (deleteTarget.type === "industry") fetchIndustries();
        else if (deleteTarget.type === "category") fetchCategories();
        else setTriggerRefresh((p) => p + 1);
      }
    } finally {
      setDeleteLoading(false); setConfirmDeleteOpen(false); setDeleteTarget(null);
    }
  };

  const openEditDialog = (type: "industry" | "category", id: number, name: string) => {
    setEditTarget({ type, id, name });
    setEditName(name);
    setEditOpen(true);
  };

  const handleEdit = async () => {
    if (!editTarget || !editName.trim()) return;
    setEditLoading(true);
    try {
      const url = editTarget.type === "industry"
        ? `${networkUrls.industryTrackContentIndustries}/${editTarget.id}`
        : `${networkUrls.industryTrackContentCategories}/${editTarget.id}`;
      const res = await Put(url, { name: editName.trim() }, true);
      if (res?.status === 200) {
        showSnackbar("success", res.response?.message || `${editTarget.type === "industry" ? "Industry" : "Category"} updated successfully`);
        if (editTarget.type === "industry") fetchIndustries();
        else fetchCategories();
        setEditOpen(false);
        setEditTarget(null);
      }
    } finally { setEditLoading(false); }
  };

  const handleEditDoc = async () => {
    if (!editDocTarget) return;
    setEditDocLoading(true);
    try {
      const res = await Put(
        `${networkUrls.industryTrackContentDocuments}/${editDocTarget.doc_id}`,
        { title: editDocTarget.title, industry_name: editDocTarget.industry_name, category: editDocTarget.category, file_name: editDocTarget.file_name },
        true
      );
      if (res?.status === 200) {
        showSnackbar("success", res.response?.message || "Document updated successfully");
        setEditDocOpen(false);
        setEditDocTarget(null);
        setTriggerRefresh((p) => p + 1);
      }
    } finally { setEditDocLoading(false); }
  };

  const handleUpload = async () => {
    const errors: typeof fieldErrors = {};
    if (!selectedIndustry) errors.industry = "Please select an industry";
    if (!selectedCategory) errors.category = "Please select a category";
    if (!selectedFile) errors.file = showTranscript ? "Please select a video file" : "Please select a file";
    if (showTranscript && !selectedTranscriptFile) errors.transcript = "Please select a transcript file";
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setUploadLoading(true);
    const fd = new FormData();
    fd.append("file", selectedFile!);
    fd.append("industry_name", selectedIndustry);
    fd.append("category", selectedCategory);
    if (showTranscript && selectedTranscriptFile) {
      fd.append("transcript_file", selectedTranscriptFile);
    }
    try {
      const res = await PostUpload(networkUrls.industryTrackContentUpload, fd, true, true);
      if (res?.status === 200) {
        showSnackbar("success", "File uploaded successfully");
        setSelectedFile(null); setSelectedTranscriptFile(null); setSelectedIndustry(""); setSelectedCategory("");
        setFieldErrors({});
        setTriggerRefresh((p) => p + 1);
      }
    } finally { setUploadLoading(false); }
  };

  const columns = [
    {
      field: "file_name", headerName: "File Name", flex: 2, minWidth: 200,
      renderCell: (params: any) => (
        <Tooltip title={`Click to view summary: ${params.value || ""}`} placement="top">
          <Box
            onClick={() => handleDocumentSummary(params.row.doc_id)}
            sx={{
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "100%",
              cursor: "pointer", color: ACCENT, fontWeight: 600,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {params.value}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "industry_name", headerName: "Industry Name", flex: 1, minWidth: 150,
      renderCell: (params: any) => (
        <Tooltip title={params.value || ""} placement="top">
          <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "100%" }}>
            {params.value}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "category", headerName: "Category", flex: 1, minWidth: 130,
      renderCell: (params: any) => (
        <Tooltip title={params.value || ""} placement="top">
          <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "100%" }}>
            {params.value}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      sortable: false,
      renderCell: (params: any) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {params.row.drive_link && (
            <Tooltip title="View in Drive">
              <IconButton
                size="small"
                sx={{ color: ACCENT, "&:hover": { backgroundColor: ACCENT_LIGHT } }}
                onClick={() => window.open(params.row.drive_link, "_blank")}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Edit">
            <IconButton
              size="small"
              sx={{ color: ACCENT, "&:hover": { backgroundColor: ACCENT_LIGHT } }}
              onClick={() => {
                setEditDocTarget({
                  doc_id: params.row.doc_id,
                  title: params.row.title || params.row.file_name || "",
                  industry_name: params.row.industry_name || "",
                  category: params.row.category || "",
                  file_name: params.row.file_name || "",
                });
                setEditDocOpen(true);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              sx={{ color: "#e53935", "&:hover": { backgroundColor: "#fce8e8" } }}
              onClick={() => openDeleteConfirm("document", params.row.doc_id, params.row.file_name)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const tableRows = documents.map((doc, index) => ({
    id: index + 1 + page * pageSize,
    ...doc,
  }));

  return (
    <Box sx={{ pr: { xs: 0, md: "20px" } }}>

      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight="700" color="#1a1a1a" sx={{ mb: 0.5 }}>
          Industry Tracks
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage industry content, categories, and upload documents for each industry track
        </Typography>
      </Box>

      {/* Upload Section */}
      <SectionCard sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Box
            sx={{
              width: 32, height: 32, borderRadius: "8px",
              backgroundColor: ACCENT_LIGHT,
              display: "flex", alignItems: "center", justifyContent: "center", color: ACCENT,
            }}
          >
            <CloudUploadIcon fontSize="small" />
          </Box>
          <Typography fontWeight="700" fontSize="0.95rem" color="#1a1a1a">
            Upload Document
          </Typography>
        </Box>
        <Divider sx={{ mb: 2.5 }} />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1.5fr 1.5fr 2fr auto" },
            gap: 2,
            alignItems: "flex-start",
          }}
        >
          {/* Industry Dropdown */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="caption" fontWeight="600" color="#555" sx={{ mb: 0.5, lineHeight: "20px" }}>
              Industry Name <span style={{ color: "red" }}>*</span>
            </Typography>
            <FormControl fullWidth size="small" error={!!fieldErrors.industry}>
              <Select
                displayEmpty
                value={selectedIndustry}
                onChange={(e) => { setSelectedIndustry(e.target.value); setFieldErrors((p) => ({ ...p, industry: undefined })); }}
                sx={{ borderRadius: "8px", fontSize: "0.85rem", height: "40px", ...(fieldErrors.industry && { "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d32f2f" } }) }}
                renderValue={(val) => val || <span style={{ color: "#888" }}>Select Industry</span>}
              >
                <MenuItem value="None" sx={{ fontSize: "0.85rem" }}>None</MenuItem>
                {industries.length === 0 ? (
                  <MenuItem disabled>No industries available</MenuItem>
                ) : (
                  industries.map((ind) => (
                    <MenuItem key={ind.id} value={ind.name} sx={{ fontSize: "0.85rem", display: "flex", justifyContent: "space-between", gap: 1 }}>
                      <span style={{ flex: 1 }}>{ind.name}</span>
                      <Box sx={{ display: "flex", gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditDialog("industry", ind.id, ind.name); }} sx={{ color: ACCENT, p: 0.3, "&:hover": { backgroundColor: ACCENT_LIGHT } }}>
                          <EditIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); openDeleteConfirm("industry", ind.id, ind.name); }} sx={{ color: "#e53935", p: 0.3, "&:hover": { backgroundColor: "#fce8e8" } }}>
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            {fieldErrors.industry && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {fieldErrors.industry}
              </Typography>
            )}
          </Box>

          {/* Category Dropdown */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="caption" fontWeight="600" color="#555" sx={{ mb: 0.5, lineHeight: "20px" }}>
              Category <span style={{ color: "red" }}>*</span>
            </Typography>
            <FormControl fullWidth size="small" error={!!fieldErrors.category}>
              <Select
                displayEmpty
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setSelectedFile(null);
                  setSelectedTranscriptFile(null);
                  setFieldErrors((p) => ({ ...p, category: undefined }));
                }}
                sx={{ borderRadius: "8px", fontSize: "0.85rem", height: "40px" }}
                renderValue={(val) => val || <span style={{ color: "#888" }}>Select Category</span>}
              >
                {categories.length === 0 ? (
                  <MenuItem disabled>No categories available</MenuItem>
                ) : (
                  categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.name} sx={{ fontSize: "0.85rem", display: "flex", justifyContent: "space-between", gap: 1 }}>
                      <span style={{ flex: 1 }}>{cat.name}</span>
                      <Box sx={{ display: "flex", gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditDialog("category", cat.id, cat.name); }} sx={{ color: ACCENT, p: 0.3, "&:hover": { backgroundColor: ACCENT_LIGHT } }}>
                          <EditIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); openDeleteConfirm("category", cat.id, cat.name); }} sx={{ color: "#e53935", p: 0.3, "&:hover": { backgroundColor: "#fce8e8" } }}>
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            {fieldErrors.category && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {fieldErrors.category}
              </Typography>
            )}
          </Box>

          {/* File Picker */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="caption" fontWeight="600" color="#555" sx={{ mb: 0.5, lineHeight: "20px" }}>
              {showTranscript ? "Video File" : "File"} <span style={{ color: "red" }}>*</span>
            </Typography>
            <Box
              component="label"
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                border: `2px dashed ${fieldErrors.file ? "#d32f2f" : selectedFile ? ACCENT : "#d0d0d0"}`,
                borderRadius: "10px",
                p: 1.5,
                textAlign: "center",
                backgroundColor: selectedFile ? ACCENT_LIGHT : "#fafafa",
                cursor: "pointer",
                minHeight: 72,
                transition: "border-color 0.2s, background-color 0.2s",
                "&:hover": { borderColor: ACCENT, backgroundColor: ACCENT_LIGHT },
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 22, color: selectedFile ? ACCENT : "#aaa" }} />
              <Typography variant="caption" fontWeight={600} color={selectedFile ? ACCENT_DARK : "#666"}
                sx={{ wordBreak: "break-word", maxWidth: "100%", lineHeight: 1.3 }}>
                {selectedFile ? selectedFile.name : showTranscript ? "Select Video File" : "Select File"}
              </Typography>
              {!selectedFile && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem" }}>
                  {showTranscript ? "MP4" : "CSV, XLSX, PDF, DOCX, PPTX"}
                </Typography>
              )}
              <VisuallyHiddenInput
                type="file"
                accept={showTranscript ? ".mp4" : ".csv,.xlsx,.pdf,.docx,.pptx"}
                onChange={(e) => { setSelectedFile(e.target.files?.[0] || null); setFieldErrors((p) => ({ ...p, file: undefined })); e.target.value = ""; }}
              />
            </Box>
            {fieldErrors.file && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {fieldErrors.file}
              </Typography>
            )}
          </Box>

          {/* Upload Button — only shown here when transcript row is NOT visible */}
          {!showTranscript && (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="caption" sx={{ mb: 0.5, lineHeight: "20px", visibility: "hidden" }}>
                &nbsp;
              </Typography>
              <ReuseableButton
                title="Upload"
                loading={uploadLoading}
                onClick={handleUpload}
                sx={{
                  width: { xs: "100%", sm: "110px" },
                  height: "40px",
                  backgroundColor: ACCENT,
                  borderRadius: "8px",
                  "&:hover": { backgroundColor: ACCENT_DARK },
                }}
              />
            </Box>
          )}
        </Box>

        {/* Transcript File Upload — shown only for Demo Asset / Demo Videos */}
        {showTranscript && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              <Box sx={{ display: "flex", flexDirection: "column", flex: 1, maxWidth: { xs: "100%", sm: "50%" } }}>
                <Typography variant="caption" fontWeight="600" color="#555" sx={{ mb: 0.5, lineHeight: "20px" }}>
                  Transcript File <span style={{ color: "red" }}>*</span>
                </Typography>
                <Box
                  component="label"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    border: `2px dashed ${fieldErrors.transcript ? "#d32f2f" : selectedTranscriptFile ? ACCENT : "#d0d0d0"}`,
                    borderRadius: "10px",
                    p: 1.5,
                    textAlign: "center",
                    backgroundColor: selectedTranscriptFile ? ACCENT_LIGHT : "#fafafa",
                    cursor: "pointer",
                    minHeight: 72,
                    transition: "border-color 0.2s, background-color 0.2s",
                    "&:hover": { borderColor: ACCENT, backgroundColor: ACCENT_LIGHT },
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 22, color: selectedTranscriptFile ? ACCENT : "#aaa" }} />
                  <Typography variant="caption" fontWeight={600} color={selectedTranscriptFile ? ACCENT_DARK : "#666"}
                    sx={{ wordBreak: "break-word", maxWidth: "100%", lineHeight: 1.3 }}>
                    {selectedTranscriptFile ? selectedTranscriptFile.name : "Select Transcript File"}
                  </Typography>
                  {!selectedTranscriptFile && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem" }}>
                      TXT, PDF, DOCX, VTT, SRT
                    </Typography>
                  )}
                  <VisuallyHiddenInput
                    type="file"
                    accept=".txt,.pdf,.docx,.vtt,.srt"
                    onChange={(e) => { setSelectedTranscriptFile(e.target.files?.[0] || null); setFieldErrors((p) => ({ ...p, transcript: undefined })); e.target.value = ""; }}
                  />
                </Box>
                {fieldErrors.transcript && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {fieldErrors.transcript}
                  </Typography>
                )}
              </Box>

              {/* Upload Button beside transcript */}
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="caption" sx={{ mb: 0.5, lineHeight: "20px", visibility: "hidden" }}>
                  &nbsp;
                </Typography>
                <ReuseableButton
                  title="Upload"
                  loading={uploadLoading}
                  onClick={handleUpload}
                  sx={{
                    width: { xs: "100%", sm: "110px" },
                    height: "40px",
                    backgroundColor: ACCENT,
                    borderRadius: "8px",
                    "&:hover": { backgroundColor: ACCENT_DARK },
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}
      </SectionCard>

      {/* Documents Table */}
      <SectionCard>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography fontWeight="700" fontSize="0.95rem" color="#1a1a1a">
            Industry Tracks
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <MuiButton
              size="small"
              variant="contained"
              startIcon={<AddIcon fontSize="small" />}
              onClick={() => setAddIndustryOpen(true)}
              sx={{
                backgroundColor: ACCENT,
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8rem",
                height: "36px",
                px: 1.5,
                boxShadow: "none",
                "&:hover": { backgroundColor: ACCENT_DARK, boxShadow: "none" },
              }}
            >
              Add Industry
            </MuiButton>
            <MuiButton
              size="small"
              variant="contained"
              startIcon={<AddIcon fontSize="small" />}
              onClick={() => setAddCategoryOpen(true)}
              sx={{
                backgroundColor: ACCENT,
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8rem",
                height: "36px",
                px: 1.5,
                boxShadow: "none",
                "&:hover": { backgroundColor: ACCENT_DARK, boxShadow: "none" },
              }}
            >
              Add Category
            </MuiButton>
            <Input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              startDecorator={<SearchIcon sx={{ color: "white", fontSize: "1.1rem" }} />}
              placeholder="Search..."
              size="sm"
              variant="soft"
              sx={{
                width: 260,
                backgroundColor: ACCENT,
                color: "white",
                borderRadius: "8px",
                "& input": { color: "white", fontSize: "0.85rem" },
                "& input::placeholder": { color: "rgba(255,255,255,0.75)" },
              }}
            />
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {isMobile ? (
          <Box>
            {docsLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress sx={{ color: ACCENT }} />
              </Box>
            ) : tableRows.length === 0 ? (
              <Box
                sx={{
                  py: 6,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  backgroundColor: "#fafafa",
                  borderRadius: "8px",
                  border: "1px dashed #ddd",
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 40, color: "#ccc", mb: 1 }} />
                <Typography variant="body2" color="text.secondary">No documents uploaded yet</Typography>
              </Box>
            ) : (
              <>
                {tableRows.map((row: any) => (
                  <Paper
                    key={row.id}
                    elevation={0}
                    sx={{
                      mb: 1.5,
                      p: 2,
                      borderRadius: "10px",
                      border: "1px solid #e8e8e8",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                      <Typography
                        fontWeight="600" fontSize="0.9rem" color={ACCENT}
                        sx={{ wordBreak: "break-word", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                        onClick={() => handleDocumentSummary(row.doc_id)}
                      >
                        {row.file_name || "----"}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
                        <Chip label={row.industry_name || "----"} size="small" sx={{ backgroundColor: ACCENT_LIGHT, color: ACCENT_DARK, fontWeight: 600, fontSize: "0.7rem" }} />
                        <Chip label={row.category || "----"} size="small" sx={{ backgroundColor: "#f0faf4", color: "#2e7d52", fontWeight: 600, fontSize: "0.7rem" }} />
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                      {row.drive_link && (
                        <IconButton size="small" sx={{ backgroundColor: ACCENT_LIGHT, color: ACCENT }} onClick={() => window.open(row.drive_link, "_blank")}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton size="small" sx={{ backgroundColor: ACCENT_LIGHT, color: ACCENT }} onClick={() => { setEditDocTarget({ doc_id: row.doc_id, title: row.title || row.file_name || "", industry_name: row.industry_name || "", category: row.category || "", file_name: row.file_name || "" }); setEditDocOpen(true); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" sx={{ backgroundColor: "#fce8e8", color: "#e53935" }} onClick={() => openDeleteConfirm("document", row.doc_id, row.file_name)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
                <Stack direction="row" justifyContent="space-between" alignItems="center" mt={2}>
                  <MuiButton variant="outlined" size="small" sx={{ borderColor: ACCENT, color: ACCENT }} disabled={page === 0} onClick={() => setPage((p) => Math.max(p - 1, 0))}>Previous</MuiButton>
                  <Typography variant="body2" color="text.secondary">Page {page + 1} of {Math.ceil(totalCount / pageSize) || 1}</Typography>
                  <MuiButton variant="outlined" size="small" sx={{ borderColor: ACCENT, color: ACCENT }} disabled={page + 1 >= Math.ceil(totalCount / pageSize)} onClick={() => setPage((p) => p + 1)}>Next</MuiButton>
                </Stack>
              </>
            )}
          </Box>
        ) : (
          <div style={{ minHeight: 100, width: "100%" }}>
            <DataGrid
              rows={tableRows}
              columns={columns}
              rowCount={totalCount}
              paginationMode="server"
              paginationModel={{ page, pageSize }}
              onPaginationModelChange={(model) => { setPage(model.page); setPageSize(model.pageSize); }}
              loading={docsLoading}
              pageSizeOptions={[5, 10, 20]}
              disableRowSelectionOnClick
              localeText={{ noRowsLabel: "No documents uploaded yet" }}
              sx={{
                border: "none",
                borderRadius: "8px",
                "--DataGrid-containerBackground": ACCENT,
                "& .MuiDataGrid-columnHeaders": { color: "#fff", fontWeight: "bold", fontSize: "0.85rem" },
                "& .MuiDataGrid-row:hover": { backgroundColor: ACCENT_LIGHT },
                "& .MuiDataGrid-cell": { fontSize: "0.85rem" },
                "& .MuiDataGrid-footerContainer": { borderTop: "1px solid #eee" },
              }}
            />
          </div>
        )}
      </SectionCard>

      {/* Add Industry Dialog */}
      <Dialog
        open={addIndustryOpen}
        onClose={() => { setAddIndustryOpen(false); setNewIndustryName(""); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Add Industry Name</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            autoFocus
            fullWidth
            label="Industry Name"
            placeholder="e.g. Healthcare, Finance"
            value={newIndustryName}
            onChange={(e) => setNewIndustryName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleAddIndustry(); }}
            size="small"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MuiButton
            onClick={() => { setAddIndustryOpen(false); setNewIndustryName(""); }}
            sx={{ textTransform: "none", color: "#666" }}
          >
            Cancel
          </MuiButton>
          <MuiButton
            variant="contained"
            onClick={handleAddIndustry}
            disabled={addIndustryLoading || !newIndustryName.trim()}
            sx={{ textTransform: "none", backgroundColor: ACCENT, borderRadius: "8px", "&:hover": { backgroundColor: ACCENT_DARK } }}
          >
            {addIndustryLoading ? <CircularProgress size={18} color="inherit" /> : "Add Industry"}
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* Add Category Dialog */}
      <Dialog
        open={addCategoryOpen}
        onClose={() => { setAddCategoryOpen(false); setNewCategoryName(""); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Add Category</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            autoFocus
            fullWidth
            label="Category Name"
            placeholder="e.g. Case Study, Proposal"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleAddCategory(); }}
            size="small"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MuiButton
            onClick={() => { setAddCategoryOpen(false); setNewCategoryName(""); }}
            sx={{ textTransform: "none", color: "#666" }}
          >
            Cancel
          </MuiButton>
          <MuiButton
            variant="contained"
            onClick={handleAddCategory}
            disabled={addCategoryLoading || !newCategoryName.trim()}
            sx={{ textTransform: "none", backgroundColor: ACCENT, borderRadius: "8px", "&:hover": { backgroundColor: ACCENT_DARK } }}
          >
            {addCategoryLoading ? <CircularProgress size={18} color="inherit" /> : "Add Category"}
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Confirm Delete</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete{" "}
            <Box component="span" fontWeight="700" color="#1a1a1a">
              "{deleteTarget?.name}"
            </Box>
            ? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MuiButton onClick={() => setConfirmDeleteOpen(false)} sx={{ textTransform: "none", color: "#666" }}>
            Cancel
          </MuiButton>
          <MuiButton
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleteLoading}
            sx={{ textTransform: "none", borderRadius: "8px" }}
          >
            {deleteLoading ? <CircularProgress size={18} color="inherit" /> : "Delete"}
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* Edit Document Dialog */}
      <Dialog
        open={editDocOpen}
        onClose={() => { setEditDocOpen(false); setEditDocTarget(null); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Edit Document</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            fullWidth
            label="File Name"
            value={editDocTarget?.file_name || ""}
            onChange={(e) => setEditDocTarget((prev) => prev ? { ...prev, file_name: e.target.value } : prev)}
            size="small"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
          />
          {/* <TextField
            fullWidth
            label="Title"
            value={editDocTarget?.title || ""}
            onChange={(e) => setEditDocTarget((prev) => prev ? { ...prev, title: e.target.value } : prev)}
            size="small"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
          /> */}
          <FormControl fullWidth size="small">
            <InputLabel>Industry Name</InputLabel>
            <Select
              label="Industry Name"
              value={editDocTarget?.industry_name || ""}
              onChange={(e) => setEditDocTarget((prev) => prev ? { ...prev, industry_name: e.target.value } : prev)}
              sx={{ borderRadius: "8px" }}
            >
              <MenuItem value="None">None</MenuItem>
              {industries.map((ind) => (
                <MenuItem key={ind.id} value={ind.name}>{ind.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={editDocTarget?.category || ""}
              onChange={(e) => setEditDocTarget((prev) => prev ? { ...prev, category: e.target.value } : prev)}
              sx={{ borderRadius: "8px" }}
            >
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.name}>{cat.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MuiButton onClick={() => { setEditDocOpen(false); setEditDocTarget(null); }} sx={{ textTransform: "none", color: "#666" }}>
            Cancel
          </MuiButton>
          <MuiButton
            variant="contained"
            onClick={handleEditDoc}
            disabled={editDocLoading}
            sx={{ textTransform: "none", backgroundColor: ACCENT, borderRadius: "8px", "&:hover": { backgroundColor: ACCENT_DARK } }}
          >
            {editDocLoading ? <CircularProgress size={18} color="inherit" /> : "Save"}
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* Edit Industry / Category Dialog */}
      <Dialog
        open={editOpen}
        onClose={() => { setEditOpen(false); setEditTarget(null); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          Edit {editTarget?.type === "industry" ? "Industry" : "Category"}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            autoFocus
            fullWidth
            label={editTarget?.type === "industry" ? "Industry Name" : "Category Name"}
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleEdit(); }}
            size="small"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MuiButton onClick={() => { setEditOpen(false); setEditTarget(null); }} sx={{ textTransform: "none", color: "#666" }}>
            Cancel
          </MuiButton>
          <MuiButton
            variant="contained"
            onClick={handleEdit}
            disabled={editLoading || !editName.trim()}
            sx={{ textTransform: "none", backgroundColor: ACCENT, borderRadius: "8px", "&:hover": { backgroundColor: ACCENT_DARK } }}
          >
            {editLoading ? <CircularProgress size={18} color="inherit" /> : "Save"}
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* Document Summary Dialog */}
      <Dialog
        open={summaryOpen}
        onClose={() => { setSummaryOpen(false); setSummaryData(null); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {summaryLoading ? "Loading Summary..." : summaryData?.title || "Document Summary"}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {summaryLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="150px">
              <CircularProgress sx={{ color: ACCENT }} />
            </Box>
          ) : summaryData ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box>
                <Typography fontWeight="700" fontSize="0.85rem" color={ACCENT} sx={{ mb: 0.5 }}>
                  Summary
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  {summaryData.summary}
                </Typography>
              </Box>

              {summaryData.implemented_solutions?.length > 0 && (
                <Box>
                  <Typography fontWeight="700" fontSize="0.85rem" color={ACCENT} sx={{ mb: 0.5 }}>
                    Implemented Solutions
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {summaryData.implemented_solutions.map((item, i) => (
                      <Typography key={i} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.4, lineHeight: 1.6 }}>
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}

              {summaryData.key_outcomes?.length > 0 && (
                <Box>
                  <Typography fontWeight="700" fontSize="0.85rem" color={ACCENT} sx={{ mb: 0.5 }}>
                    Key Outcomes
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {summaryData.key_outcomes.map((item, i) => (
                      <Typography key={i} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.4, lineHeight: 1.6 }}>
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}

              {summaryData.challenges_addressed?.length > 0 && (
                <Box>
                  <Typography fontWeight="700" fontSize="0.85rem" color={ACCENT} sx={{ mb: 0.5 }}>
                    Challenges Addressed
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {summaryData.challenges_addressed.map((item, i) => (
                      <Typography key={i} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.4, lineHeight: 1.6 }}>
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MuiButton
            onClick={() => { setSummaryOpen(false); setSummaryData(null); }}
            sx={{ textTransform: "none", color: "#666" }}
          >
            Close
          </MuiButton>
        </DialogActions>
      </Dialog>

      {snackbarOpen && (
        <ReuseableSnackbar
          isShowSnackbar={snackbarOpen}
          colour={snackbarInfo.color}
          setIsShowSnackbar={setSnackbarOpen}
          message={snackbarInfo.message}
          autoHideDuration={2000}
        />
      )}
    </Box>
  );
};

export default IndustryTrackContent;
